/**
 * useQuestsAdmin — данные и мутации «EO → Конфигуратор → Квесты и слоты».
 * Образец — useBranchConfig: optimistic-слой + run(fn, rollback) с flashOk/
 * hint/reload, скоуп по филиалу. quests/slots/linkedGroups — useAppData;
 * справочники/цены/версии — useQuestCats. saveQuest — оркестр: PUT quests
 * (A31: crew_rule, derive operators) + bulk-нормы/ресурсы + quest_prices
 * (min_price сохраняем) + linked-groups. Слоты — PUT /api/slots/bulk.
 */
import { useCallback, useEffect, useRef, useState } from "react";
import { api } from "../api/client";
import {
  ApiException, type ApiList, type ApiOk, type Consumable, type Quest,
  type QuestConsumable, type QuestPrice, type QuestVersion, type Resource,
} from "../api/types";
import { keyOf } from "../screens/eo/slotTime";
import { useAppData } from "./useAppData";
import { useQuestCats } from "./useQuestCats";

/** Норма расходника в драфте формы (wire quest_consumables без скоупа). */
export interface ConsItem { consumable_id: string; qty: number; per_player: number }

/** Полный драфт сохранения формы квеста (кнопка «Сохранить»). */
export interface QuestSaveDraft {
  name: string; short_name: string; duration: number; reset_time: number;
  /** Состав A31 «ММЖН…»; '' = снять правило. */
  crew_rule: string;
  /** null = нормы не трогали (bulk не шлём). */
  cons: ConsItem[] | null;
  res: string[]; links: string[]; pn: number; pb: number; pp: number;
  min_price?: number; // клон: min_price исходника; иначе сохраняется текущий прайс цели
  mk_id?: string;     // ID в кабинете «Мир Квестов» — /api/mk/* принимает его наравне с нашим id
}

export interface QuestsAdmin {
  quests: Quest[];
  versions: QuestVersion[];
  consCat: Consumable[];
  resCat: Resource[];
  questRes: Record<string, string[]>;
  prices: Record<string, QuestPrice>;
  consOf: Record<string, QuestConsumable[]>;
  loadCons: (questId: string) => void;
  /** Нормы явным GET (кеш — фолбэк сети): клон без race ленивого прогрева. */
  consFor: (questId: string) => Promise<QuestConsumable[]>;
  linksOf: (questId: string) => string[];
  slotsOf: (questId: string) => string[];
  loading: boolean;
  hint: string | null;
  /** flashOk карточки: ключ `card:{id}` + счётчик n (React key хоста). */
  flash: { key: string; n: number } | null;
  createQuest: (d: { name: string; short: string; dur: number; reset: number; crew: string }) => Promise<string | null>;
  saveQuest: (q: Quest, d: QuestSaveDraft) => Promise<boolean>;
  deleteQuest: (q: Quest) => void;
  saveSlots: (questId: string, times: string[]) => void;
  bindVersion: (v: QuestVersion, q: Quest) => void;
  unbindVersion: (v: QuestVersion) => void;
  refetch: () => void; // pull-to-refresh (A43.3): справочники + перезапрос норм
}

export function useQuestsAdmin(branchId: string): QuestsAdmin {
  const app = useAppData();
  const [localTick, setLocalTick] = useState(0);
  const { cats, setCats, consOf, setConsOf, loadCons, consFor, resetCons } = useQuestCats(branchId, localTick);
  const [qOver, setQOver] = useState<Record<string, Partial<Quest>>>({});
  const [phantom, setPhantom] = useState<Quest[]>([]);
  const [gone, setGone] = useState<string[]>([]);
  const [slotsOver, setSlotsOver] = useState<Record<string, string[]>>({});
  const [linksOver, setLinksOver] = useState<Record<string, string[]>>({});
  const [hint, setHint] = useState<string | null>(null);
  const [flash, setFlash] = useState<{ key: string; n: number } | null>(null);
  const pendingRef = useRef(0), flashT = useRef(0), branchRef = useRef(branchId);
  branchRef.current = branchId;

  const resetOver = () => { setQOver({}); setPhantom([]); setGone([]); setSlotsOver({}); setLinksOver({}); };
  /* Смена филиала — оптимистика/hint/флэш неактуальны (useBranchConfig). */
  useEffect(() => { resetOver(); setHint(null); setFlash(null); window.clearTimeout(flashT.current); }, [branchId]);
  /* Свежие данные приехали и мутаций в полёте нет — оптимистика не нужна. */
  useEffect(() => { if (!app.loading && pendingRef.current === 0) resetOver(); }, [app.loading]);
  useEffect(() => () => window.clearTimeout(flashT.current), []);

  const doFlash = (key: string) => {
    setFlash((f) => ({ key, n: (f?.n ?? 0) + 1 }));
    window.clearTimeout(flashT.current);
    flashT.current = window.setTimeout(() => setFlash(null), 900);
  };

  /** Обёртка мутации (useBranchConfig): flash при успехе, rollback+hint при ошибке. */
  const run = (fn: () => Promise<string | null>, rollback: () => void): Promise<boolean> => {
    setHint(null);
    const bid = branchRef.current;
    pendingRef.current += 1;
    return fn()
      .then((key) => { if (key && branchRef.current === bid) doFlash(key); return true; })
      .catch((err: unknown) => {
        rollback();
        if (branchRef.current === bid) setHint(err instanceof ApiException && err.status === 404
          ? "Не сохранилось: уже удалено другим пользователем. Список обновлён."
          : "Не сохранилось: ошибка сети. Значение возвращено.");
        return false;
      })
      .finally(() => {
        /* reload и при ошибке: честный 404 = цель удалена другим EO */
        pendingRef.current -= 1; app.reload(); setLocalTick((n) => n + 1);
      });
  };

  const quests = phantom
    .filter((p) => !app.quests.some((q) => q.id === p.id))
    .concat(app.quests
      .filter((q) => q.branch_id === branchId)
      .sort((a, z) => (a.sort_order ?? 0) - (z.sort_order ?? 0)))
    .filter((q) => !gone.includes(q.id))
    .map((q) => (qOver[q.id] ? { ...q, ...qOver[q.id] } : q));

  const linksOf = useCallback((questId: string): string[] => {
    const over = linksOver[questId];
    if (over) return over;
    const out: string[] = [];
    for (const g of app.linkedGroups) {
      if (g.branch_id !== branchId || !g.members.includes(questId)) continue;
      for (const x of g.members) if (x !== questId && !out.includes(x)) out.push(x);
    }
    return out;
  }, [app.linkedGroups, branchId, linksOver]);

  /* keyOf-сорт: API отдаёт лексический ORDER BY time, а хронология эталона —
     якорь 06:00 (js:11), ночные 00:xx в КОНЦЕ грида, не в начале. */
  const slotsOf = useCallback((questId: string): string[] =>
    slotsOver[questId] ?? app.slots.filter((s) => s.quest_id === questId && s.active)
      .map((s) => s.time).sort((a, b) => keyOf(a) - keyOf(b)),
  [app.slots, slotsOver]);

  const createQuest = async (d: { name: string; short: string; dur: number; reset: number; crew: string }) => {
    const id = `q_${Date.now().toString(36)}`;
    setPhantom((p) => [...p, {
      id, branch_id: branchId, name: d.name, short_name: d.short, duration: d.dur,
      reset_time: d.reset, operators: d.crew.length || 1, active: 1, crew_rule: d.crew || null,
    }]);
    const ok = await run(async () => {
      /* Атомарно одним POST (пара POST+PUT дублила квест при повторе после
         упавшего PUT); skip_default_slots — пустой грид эталона (js:191). */
      await api<ApiOk>("/api/quests", { method: "POST", body: {
        id, branch_id: branchId, name: d.name, short_name: d.short,
        duration: d.dur, reset_time: d.reset, operators: d.crew.length || 1,
        crew_rule: d.crew, skip_default_slots: true,
      } });
      return `card:${id}`;
    }, () => setPhantom((p) => p.filter((x) => x.id !== id)));
    return ok ? id : null;
  };

  const saveQuest = (q: Quest, d: QuestSaveDraft): Promise<boolean> => {
    const patch: Partial<Quest> = {
      name: d.name, short_name: d.short_name, duration: d.duration, reset_time: d.reset_time,
      crew_rule: d.crew_rule || null, operators: d.crew_rule ? d.crew_rule.length : q.operators,
    };
    setQOver((o) => ({ ...o, [q.id]: patch }));
    return run(async () => {
      await api<ApiOk>(`/api/quests/${q.id}`, { method: "PUT", body: {
        name: d.name, short_name: d.short_name, duration: d.duration,
        reset_time: d.reset_time, crew_rule: d.crew_rule, ...(d.mk_id !== undefined ? { mk_id: d.mk_id } : {}),
      } });
      if (d.cons) {
        await api<ApiOk>("/api/quest-consumables/bulk", { method: "PUT", body: { quest_id: q.id, items: d.cons } });
        setConsOf((m) => ({ ...m, [q.id]: d.cons!.map((c) => ({ quest_id: q.id, version_id: null, ...c })) }));
      }
      await api<ApiOk>("/api/quest-resources/bulk", { method: "PUT", body: { quest_id: q.id, resource_ids: d.res } });
      await api<ApiOk>("/api/prices/quests", { method: "POST", body: { prices: [{
        quest_id: q.id, base_price: d.pb, threshold: d.pn, extra_per_player: d.pp,
        min_price: d.min_price ?? cats.prices[q.id]?.min_price ?? 0,
      }] } });
      const next = [...new Set(d.links)].sort();
      if ([...linksOf(q.id)].sort().join(",") !== next.join(",")) {
        for (const g of app.linkedGroups.filter((x) => x.branch_id === branchId && x.members.includes(q.id))) {
          await api<ApiOk>(`/api/linked-groups/${g.id}`, { method: "DELETE" });
        }
        if (next.length) {
          /* q.id мог ещё не приехать в app.quests (клон) — имя из драфта. */
          const name = [q.id, ...next]
            .map((id) => (id === q.id ? d.short_name : app.quests.find((x) => x.id === id)?.short_name ?? id))
            .join(" · ").slice(0, 100);
          await api<ApiOk>("/api/linked-groups", { method: "POST", body: { branch_id: branchId, name, quests: [q.id, ...next] } });
        }
        setLinksOver((o) => ({ ...o, [q.id]: next }));
      }
      return `card:${q.id}`;
    }, () => setQOver((o) => {
      if (o[q.id] !== patch) return o; // поверх уже более новый драфт
      const n = { ...o }; delete n[q.id]; return n;
    }));
  };

  const deleteQuest = (q: Quest) => {
    setGone((g) => [...g, q.id]);
    void run(async () => {
      /* отвязка спецверсий (js:369) — атомарно на сервере внутри DELETE */
      await api<ApiOk>(`/api/quests/${q.id}`, { method: "DELETE" }); return null;
    }, () => setGone((g) => g.filter((x) => x !== q.id)));
  };

  const saveSlots = (questId: string, times: string[]) => {
    setSlotsOver((o) => ({ ...o, [questId]: times }));
    void run(async () => {
      await api<ApiOk>("/api/slots/bulk", { method: "PUT", body: { quest_id: questId, times } });
      return `card:${questId}`;
    }, () => setSlotsOver((o) => { const n = { ...o }; delete n[questId]; return n; }));
  };

  /* Привязка спецверсии из формы квеста; первая — prefill с базового (A35). */
  const bindVersion = (v: QuestVersion, q: Quest) => {
    setCats((c) => ({ ...c, versions: c.versions.map((x) => (x.id === v.id
      ? { ...x, slot_quest_id: q.id, base_short: q.short_name, base_duration: q.duration } : x)) }));
    void run(async () => {
      const body: Record<string, unknown> = { slot_quest_id: q.id };
      if (!v.slot_quest_id) {
        body.operators_comp = q.crew_rule || "Н".repeat(Math.max(1, q.operators || 1));
        const p = cats.prices[q.id];
        if (p) { body.base_players = p.threshold; body.base_price = p.base_price; body.price_per_extra = p.extra_per_player; }
      }
      await api<ApiOk>(`/api/quest-versions/${v.id}`, { method: "PUT", body });
      if (!v.slot_quest_id) {
        /* Нормы базового — явным GET (prefillSpec js:47 копирует всегда):
           ленивый кеш consOf мог не успеть; кеш — фолбэк сетевой ошибки. */
        const wire = (rows: QuestConsumable[]) =>
          rows.map((c) => ({ consumable_id: c.consumable_id, qty: c.qty, per_player: c.per_player }));
        const items = await api<ApiList<QuestConsumable>>(`/api/quest-consumables?quest=${encodeURIComponent(q.id)}`)
          .then((r) => wire(r.results ?? [])).catch(() => wire(consOf[q.id] ?? []));
        if (items.length) await api<ApiOk>("/api/quest-consumables/bulk", { method: "PUT", body: { quest_id: q.id, version_id: v.id, items } });
      }
      return `card:${q.id}`;
    }, () => setCats((c) => ({ ...c, versions: c.versions.map((x) => (x.id === v.id ? v : x)) })));
  };

  const unbindVersion = (v: QuestVersion) => {
    setCats((c) => ({ ...c, versions: c.versions.map((x) => (x.id === v.id
      ? { ...x, slot_quest_id: null, base_short: null, base_duration: null } : x)) }));
    void run(async () => {
      await api<ApiOk>(`/api/quest-versions/${v.id}`, { method: "PUT", body: { slot_quest_id: null } });
      return null;
    }, () => setCats((c) => ({ ...c, versions: c.versions.map((x) => (x.id === v.id ? v : x)) })));
  };

  return {
    quests, versions: cats.versions, consCat: cats.cons, resCat: cats.res,
    questRes: cats.questRes, prices: cats.prices, consOf, loadCons, consFor, linksOf, slotsOf,
    loading: (app.loading && app.quests.length === 0) || (!!branchId && !cats.loaded),
    hint, flash, createQuest, saveQuest, deleteQuest, saveSlots, bindVersion, unbindVersion,
    refetch: () => { resetCons(); setLocalTick((n) => n + 1); },
  };
}
